import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Input } from "../../../../../common/Input";

const EmployeeTypeAddEditModal = ({ isOpen, onClose, selectedItem, onSave, mode }) => {
  const [form, setForm] = useState({
    code: "", description: "", isActive: true,
    isOperationBenefitEligible: false, isPartialSalaryEligible: false,
    allowFixedLogs: false, leaveIncentiveFixDays: 0, leaveIncentiveAllowedAbsents: 0,
    maintenanceSalaryCombinationDisregardOtherFilter: true,
  });

  const [errorState, setErrorState] = useState({
    code: false, description: false, leaveIncentiveFixDays: false, leaveIncentiveAllowedAbsents: false,
  });

  useEffect(() => {
    if (isOpen) {
      if (selectedItem) {
        setForm({ ...selectedItem });
      } else {
        setForm({
          code: "", description: "", isActive: true,
          isOperationBenefitEligible: false, isPartialSalaryEligible: false,
          allowFixedLogs: false, leaveIncentiveFixDays: 0, leaveIncentiveAllowedAbsents: 0,
          maintenanceSalaryCombinationDisregardOtherFilter: true,
        });
      }
      setErrorState({ code: false, description: false, leaveIncentiveFixDays: false, leaveIncentiveAllowedAbsents: false });
    }
  }, [isOpen, selectedItem]);

  const handleInputChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const submit = (e) => {
    e.preventDefault();
    const newErrors = {
      code: !form.code,
      description: !form.description,
      leaveIncentiveFixDays: form.leaveIncentiveFixDays === "" || form.leaveIncentiveFixDays === null,
      leaveIncentiveAllowedAbsents: form.leaveIncentiveAllowedAbsents === "" || form.leaveIncentiveAllowedAbsents === null,
    };
    if (Object.values(newErrors).some(v => v)) {
      setErrorState(newErrors);
      return;
    }
    onSave(form);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto">
      <div className="bg-gray-50 w-full md:w-[90%] lg:w-[70%] xl:w-[60%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800">{mode === "add" ? "Add Employee Type" : "Update Employee Type"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none cursor-pointer"><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-grow space-y-4">

          {/* Employee Type Information */}
          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
            <div className="py-1 border-b border-gray-300"><h3 className="font-medium text-gray-700">Employee Type Information</h3></div>
            <div className="grid grid-cols-1 gap-4 pb-3 pt-3">
              <Input label="Code:" value={form.code} onChange={(e) => handleInputChange('code', e.target.value)} isRequired error={errorState.code && !form.code} errorMessage="Please fill up this field" />
              <Input label="Description:" value={form.description} onChange={(e) => handleInputChange('description', e.target.value)} isRequired error={errorState.description && !form.description} errorMessage="Please fill up this field" />
              <div className="flex items-center pt-2">
                <input type="checkbox" checked={form.isActive} onChange={(e) => handleInputChange('isActive', e.target.checked)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <label className="ml-2 text-sm font-medium text-gray-600">Active</label>
              </div>
            </div>
          </div>

          {/* Leave Incentive Config */}
          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
            <div className="py-1 border-b border-gray-300"><h3 className="font-medium text-gray-700">Leave Incentive Config</h3></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-3 pt-3">
              <Input label="Leave Incentive Fix Days:" type="number" value={form.leaveIncentiveFixDays} onChange={(e) => handleInputChange('leaveIncentiveFixDays', e.target.value)} isRequired error={errorState.leaveIncentiveFixDays} />
              <Input label="Leave Incentive Allowed Absents:" type="number" value={form.leaveIncentiveAllowedAbsents} onChange={(e) => handleInputChange('leaveIncentiveAllowedAbsents', e.target.value)} isRequired error={errorState.leaveIncentiveAllowedAbsents} />
            </div>
          </div>

          {/* Individual Config Sections */}
          {[
            { title: "Fix Logs Config", field: "allowFixedLogs", label: "Allow Fixed Logs" },
            { title: "Partial Salary Config", field: "isPartialSalaryEligible", label: "Eligible for Partial Salary" },
            { title: "Operation Benefit Config", field: "isOperationBenefitEligible", label: "Eligible for Operation Benefit" },
            { title: "Maintenance Salary Combination Config", field: "maintenanceSalaryCombinationDisregardOtherFilter", label: "Disregard Other Filter?" }
          ].map((sec) => (
            <div key={sec.field} className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300"><h3 className="font-medium text-gray-700">{sec.title}</h3></div>
              <div className="grid grid-cols-1 gap-4 pb-3 pt-3">
                <div className="flex items-center pt-2">
                  <input type="checkbox" checked={form[sec.field]} onChange={(e) => handleInputChange(sec.field, e.target.checked)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  <label className="ml-2 text-sm font-medium text-gray-600">{sec.label}</label>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors">Cancel</button>
          <button onClick={submit} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors">
            {mode === "edit" ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeTypeAddEditModal;